import { Outlet, useLocation } from 'react-router-dom';
import { AdminGuard } from '../../components/guards/AdminGuard';
import { TabBar, type TabItem } from '../../components/admin/TabBar';
import { useAuthStore } from '../../store/authStore';
import type { UserRole } from '../../types';

// Each tab declares the roles allowed to see it. ADMIN is treated as a
// superset by hasAnyRole, so admin sees every tab regardless of listing.
type RoleScopedTab = TabItem & { roles: UserRole[] };

const MAIN_TABS: RoleScopedTab[] = [
  { key: 'overview',            label: 'Overview',           to: '/admin/overview',           roles: ['ADMIN'] },
  { key: 'syncs',               label: 'Sync Jobs',          to: '/admin/syncs',              roles: ['ADMIN'] },
  { key: 'backups',             label: 'Backups',            to: '/admin/backups',            roles: ['ADMIN'] },
  { key: 'audit',               label: 'Audit Logs',         to: '/admin/audit/timeline',     roles: ['ADMIN'] },
  { key: 'matching',            label: 'Matching',           to: '/admin/matching',           roles: ['ADMIN', 'MATCHING_SUPPORT'] },
  { key: 'imports',             label: 'Imports',            to: '/admin/imports',            roles: ['ADMIN', 'MATCHING_SUPPORT'] },
  { key: 'competitor-scraping', label: 'Competitor Scrape',  to: '/admin/competitor-scraping', roles: ['ADMIN'] },
  { key: 'sport-seasons',       label: 'Sport Seasons',      to: '/admin/sport-seasons',      roles: ['ADMIN'] },
  { key: 'regions',             label: 'Sport Season Regions', to: '/admin/regions',          roles: ['ADMIN'] },
  { key: 'minicrm-migration',   label: 'MiniCRM Migration',  to: '/admin/minicrm-migration',  roles: ['ADMIN'] },
  { key: 'users',               label: 'Users',              to: '/admin/users',              roles: ['ADMIN'] },
  { key: 'sales-teams',         label: 'Sales Teams',        to: '/admin/sales-teams',        roles: ['ADMIN'] },
];

const AUDIT_SUB_TABS: TabItem[] = [
  { key: 'timeline',  label: 'Timeline',   to: '/admin/audit/timeline' },
  { key: 'by-user',   label: 'By User',    to: '/admin/audit/by-user' },
  { key: 'by-entity', label: 'By Entity',  to: '/admin/audit/by-entity' },
];

function deriveActiveMain(pathname: string): string {
  if (pathname.startsWith('/admin/audit')) return 'audit';
  const seg = pathname.replace(/^\/admin\/?/, '').split('/')[0];
  return seg || 'overview';
}

function deriveActiveAudit(pathname: string): string {
  const seg = pathname.replace(/^\/admin\/audit\/?/, '').split('/')[0];
  return seg || 'timeline';
}

export function AdminLayout() {
  const { pathname } = useLocation();
  const { hasAnyRole } = useAuthStore();
  const activeMain = deriveActiveMain(pathname);
  const showAuditStrip = pathname.startsWith('/admin/audit');
  const visibleTabs = MAIN_TABS.filter((t) => hasAnyRole(...t.roles));

  return (
    <AdminGuard>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <TabBar tabs={visibleTabs} activeKey={activeMain} size="lg" />
        {showAuditStrip && (
          <TabBar tabs={AUDIT_SUB_TABS} activeKey={deriveActiveAudit(pathname)} size="sm" />
        )}
        <div className="pt-6">
          <Outlet />
        </div>
      </div>
    </AdminGuard>
  );
}
